import { Injectable } from '@angular/core';

import qrcodegen from './qrcodegen';

export const ERROR_LEVEL_MAP: Record<'L' | 'M' | 'Q' | 'H', qrcodegen.QrCode.Ecc> = {
  L: qrcodegen.QrCode.Ecc.LOW,
  M: qrcodegen.QrCode.Ecc.MEDIUM,
  Q: qrcodegen.QrCode.Ecc.QUARTILE,
  H: qrcodegen.QrCode.Ecc.HIGH,
} as const;

export const DEFAULT_SIZE = 160;
export const DEFAULT_SCALE = 10;
export const DEFAULT_PADDING = 10;
export const DEFAULT_COLOR = '#000000';
export const DEFAULT_BACKGROUND_COLOR = '#FFFFFF';
export const DEFAULT_ICONSIZE = 40;
export const DEFAULT_LEVEL: keyof typeof ERROR_LEVEL_MAP = 'M';

@Injectable({
  providedIn: 'root',
})
export class QrGeneratorService {
  drawCanvas(
    canvas: HTMLCanvasElement,
    value: qrcodegen.QrCode | null,
    size = DEFAULT_SIZE,
    scale = DEFAULT_SCALE,
    padding: number | number[] = DEFAULT_PADDING,
    color = DEFAULT_COLOR,
    backgroundColor = DEFAULT_BACKGROUND_COLOR,
    iconSize = DEFAULT_ICONSIZE,
    icon?: string
  ): void {
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    const formattedPadding = this.formatPadding(padding);
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;
    if (!value) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      return;
    }
    canvas.width = value.size * scale + formattedPadding[1] + formattedPadding[3];
    canvas.height = value.size * scale + formattedPadding[0] + formattedPadding[2];
    if (!icon) {
      this.drawCanvasBackground(ctx, canvas.width, canvas.height, scale, backgroundColor);
      this.drawCanvasColor(ctx, value, scale, formattedPadding, backgroundColor, color);
    } else {
      const iconImg = new Image();
      iconImg.src = icon;
      iconImg.crossOrigin = 'anonymous';
      iconImg.width = iconSize * (canvas.width / size);
      iconImg.height = iconSize * (canvas.width / size);
      iconImg.onload = () => {
        this.drawCanvasBackground(ctx, canvas.width, canvas.height, scale, backgroundColor);
        this.drawCanvasColor(ctx, value, scale, formattedPadding, backgroundColor, color);
        const iconCoordinate = canvas.width / 2 - (iconSize * (canvas.width / size)) / 2;

        ctx.fillStyle = backgroundColor;
        ctx.fillRect(iconCoordinate, iconCoordinate, iconSize * (canvas.width / size), iconSize * (canvas.width / size));
        ctx.drawImage(
          iconImg,
          iconCoordinate + 10,
          iconCoordinate + 10,
          iconSize * (canvas.width / size) - 20,
          iconSize * (canvas.width / size) - 20
        );
      };
      iconImg.onerror = () => {
        this.drawCanvasBackground(ctx, canvas.width, canvas.height, scale, backgroundColor);
        this.drawCanvasColor(ctx, value, scale, formattedPadding, backgroundColor, color);
      };
    }
  }

  plotQRCodeData = (
    value: string,
    level: keyof typeof ERROR_LEVEL_MAP = DEFAULT_LEVEL
  ): qrcodegen.QrCode | null => {
    if (!value) {
      return null;
    }
    return qrcodegen.QrCode.encodeText(value, ERROR_LEVEL_MAP[level]);
  };

  private drawCanvasColor(
    ctx: CanvasRenderingContext2D,
    value: qrcodegen.QrCode,
    scale: number,
    padding: number[],
    backgroundColor: string,
    color: string
  ): void {
    for (let y = 0; y < value.size; y++) {
      for (let x = 0; x < value.size; x++) {
        ctx.fillStyle = value.getModule(x, y) ? color : backgroundColor;
        ctx.fillRect(padding[3] + x * scale, padding[0] + y * scale, scale, scale);
      }
    }
  }

  private drawCanvasBackground(
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    scale: number,
    backgroundColor: string
  ): void {
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(x * scale, y * scale, scale, scale);
      }
    }
  }

  private formatPadding(padding: number | number[]): number[] {
    if (Array.isArray(padding)) {
      // Build an array of 4 elements and repeat values from padding as necessary to set the value of the array
      return Array(4)
        .fill(0)
        .map((_, index) => padding[index % padding.length]);
    } else {
      return [padding, padding, padding, padding];
    }
  }
}
